import { useMutation, useQueryClient } from '@tanstack/react-query';

import { UserConnectionStatus } from '@shared/types/userConnection';

import API from '@api/API';

import { useUserConnectionsQuery } from '@queries/UserConnectionsQueries';
import { useCurrentUser } from '@queries/UserQueries';

export const useInviteConnectionMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ otherUserId, eventId }: { otherUserId: string; eventId: string }) =>
      API.post(`/v1/users/${otherUserId}/events/${eventId}/invite`, {}),
  });

  return {
    isInvitingConnection: isPending,
    inviteConnectionAsync: mutateAsync,
  };
};

export const useSendConnectionRequest = () => {
  const { currentUser } = useCurrentUser();
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ toUserId, autoAcceptKey }: { toUserId: string; autoAcceptKey?: string }) =>
      API.post(`/v1/user/connections/request`, { toUserId, autoAcceptKey }),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: useUserConnectionsQuery.queryKey(currentUser?._id),
      });
    },
  });

  return {
    isSendingConnectionRequest: isPending,
    sendConnectionRequestAsync: mutateAsync,
  };
};

export const useRespondToConnectionRequest = () => {
  const { currentUser } = useCurrentUser();
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({
      connectionId,
      status,
    }: {
      connectionId: string;
      status: UserConnectionStatus.Accepted | UserConnectionStatus.Rejected;
    }) => API.post(`/v1/user/connections/respond`, { connectionId, status }),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: useUserConnectionsQuery.queryKey(currentUser?._id),
      });
    },
  });

  return {
    isRespondingToConnectionRequest: isPending,
    respondToConnectionRequestAsync: mutateAsync,
  };
};
